import React, { useState } from "react";
import "./index.css";
import Container from "../../../Components/General/Container";
import { aceItValues, toStyle, anchorTop } from "./aceItData";

const NOW_PLAYER_IMG = "https://images.nowpurchase.com/uploads/";

// Newest quarter first — the chip row reads left to right in this order.
//
// `crop` mirrors the image fill's framing in Figma: the photo is absolutely
// positioned inside the square and scaled so the visible window matches the
// design. These are percentages of the card, so they hold at any card size.
// Omit `crop` for a plain cover fill.
const quarters = [
  {
    quarter: "JFM’26",
    name: "Kanishk Sharma",
    img: `${NOW_PLAYER_IMG}20260722074521_kanishk.jpg`,
    crop: { width: 195.19, height: 266.95, left: -52.41, top: -111.16 },
  },
  {
    quarter: "OND’25",
    name: "Milap Barai",
    img: `${NOW_PLAYER_IMG}20260722085436_milap_hi.jpg`,
    crop: { width: 334.62, height: 446.16, left: -109.29, top: -180.46 },
  },
  {
    quarter: "JAS’25",
    name: "Amiya Paul",
    img: `${NOW_PLAYER_IMG}20260722074519_amiya.jpg`,
  },
  {
    quarter: "JFM’25",
    name: "Sivasankar C",
    img: `${NOW_PLAYER_IMG}20260722074519_sivasankar.jpg`,
  },
  {
    quarter: "OND’24",
    name: "Mayukh Bhattacharjee",
    img: `${NOW_PLAYER_IMG}20260722074518_mayukh.jpg`,
    crop: { width: 100, height: 193.06, left: 0, top: -2.06 },
  },
  {
    quarter: "JAS’24",
    name: "Pabitra Saha",
    img: `${NOW_PLAYER_IMG}20260722074517_pabitra.jpg`,
    crop: { width: 165.59, height: 116.24, left: -25.45, top: -16.04 },
  },
  {
    quarter: "AMJ’24",
    name: "Arghyadeep",
    img: `${NOW_PLAYER_IMG}20260722074441_arghyadeep.jpg`,
  },
];


function NowPlayers() {
  // Two quarters are always on screen: the one that was active and the one
  // just picked. Their chips club together into a single pill, which is the
  // interaction the Figma variants show (node 11886:1137).
  const [pair, setPair] = useState([0, 1]);
  const [activeValue, setActiveValue] = useState(null);

  const selectQuarter = (index) => {
    // Picking a quarter that is already on screen collapses the pair down to
    // that one — either half of a merged pill, not just the current quarter.
    // Anything else clubs the outgoing and incoming quarters together.
    setPair((prev) =>
      prev.includes(index) ? [index, index] : [prev[1], index]
    );
  };

  // Chips render in list order, so the merged pill sits where the earlier of
  // the two quarters would have been.
  const [first, second] = [...pair].sort((a, b) => a - b);
  const merged = first !== second;

  const shownPlayers = merged
    ? [quarters[first], quarters[second]]
    : [quarters[first]];

  const activeCube = aceItValues.find((value) => value.key === activeValue);

  return (
    <div
      className={`nowPlayersSectionWrap animated-section section${
        activeCube ? " hasActiveValue" : ""
      }`}
    >
      <Container>
        <div className="nowPlayersRow">
          <div className="nowPlayersMain aosAnim">
            <h2 className="nowPlayersTitle">
              Check out our{" "}
              <span className="gradientText">NowPlayers!</span>
            </h2>

            <div className="nowPlayerCards">
              {shownPlayers.map((player) => (
                <figure className="nowPlayerCard" key={player.quarter}>
                  <div className="nowPlayerImg">
                    <img
                      loading="lazy"
                      src={player.img}
                      alt={player.name}
                      style={
                        player.crop
                          ? {
                              position: "absolute",
                              width: `${player.crop.width}%`,
                              height: `${player.crop.height}%`,
                              left: `${player.crop.left}%`,
                              top: `${player.crop.top}%`,
                              maxWidth: "none",
                              objectFit: "fill",
                            }
                          : undefined
                      }
                    />
                  </div>
                  <figcaption>
                    <strong>{player.name}</strong>
                    <span>{player.quarter}</span>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="nowPlayerChips" role="tablist">
              {quarters.map((item, index) => {
                // The second half of a merged pair is swallowed by the pill
                // rendered at the first half's slot.
                if (merged && index === second) return null;

                if (merged && index === first) {
                  return (
                    <span className="nowPlayerChip isMerged" key="merged">
                      <button
                        type="button"
                        role="tab"
                        aria-selected="true"
                        onClick={() => selectQuarter(first)}
                      >
                        {quarters[first].quarter}
                      </button>
                      <i aria-hidden="true">-</i>
                      <button
                        type="button"
                        role="tab"
                        aria-selected="true"
                        onClick={() => selectQuarter(second)}
                      >
                        {quarters[second].quarter}
                      </button>
                    </span>
                  );
                }

                return (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={index === first}
                    key={item.quarter}
                    className={`nowPlayerChip${
                      index === first ? " isActive" : ""
                    }`}
                    onClick={() => selectQuarter(index)}
                  >
                    {item.quarter}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            className="aceItTower"
            aria-label="Our ACE IT values"
            onMouseLeave={() => setActiveValue(null)}
          >
            {aceItValues.map((value, index) => (
              <button
                type="button"
                key={value.key}
                // A cube sits on the one below it, so the higher cube has to
                // paint over the lower one's top face: earlier entries (higher
                // in the tower) get the larger z-index.
                style={{
                  ...toStyle(value.rect),
                  zIndex: aceItValues.length - index,
                }}
                aria-pressed={activeValue === value.key}
                className={`aceItCube${
                  activeValue === value.key ? " isActive" : ""
                }`}
                // Hover drives the callout on pointer devices; click and focus
                // keep it reachable on touch and by keyboard. None of these
                // toggle: a tap fires mouseenter, then focus, then click, so a
                // toggle here would always close what the first two opened.
                // Closing is handled by the scrim and by leaving the tower.
                onMouseEnter={() => setActiveValue(value.key)}
                onFocus={() => setActiveValue(value.key)}
                onClick={() => setActiveValue(value.key)}
              >
                <img src={value.img} alt={value.title} />
              </button>
            ))}

            {activeCube && (
              <div
                className="aceItCallout"
                style={{ top: anchorTop(activeCube.rect) }}
              >
                <span className="aceItConnector" aria-hidden="true" />
                <div className="aceItCalloutCard">
                  <h3>{activeCube.title}</h3>
                  <p>{activeCube.body}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>

      {activeCube && (
        <button
          type="button"
          className="aceItScrim"
          aria-label="Close value details"
          onClick={() => setActiveValue(null)}
        />
      )}
    </div>
  );
}

export default NowPlayers;
